import type {
  AnnotationsProps,
  BoundingBoxAnnotationProps,
  LineAnnotationProps,
  PointAnnotationProps,
} from '@repo/image-annotator/lib';

import type { ImageAnalyses } from '../../../@types/latestImagesV2';
import type { BinValues } from '../../annotator/findingSidebar/confidenceScoreFilter/atoms';

import {
  getCalculusFindings,
  getCariesFindings,
  getRestorativeAdjacentLucencyFindings,
} from './findings/box-findings';
import { getRblLines, getRblPoints } from './findings/line-findings';

// TODO: Migrate to npm package: BOSON-279
export const getFindings = (
  img: ImageAnalyses,
  options: {
    cariesBin: BinValues | undefined;
    enabledFindings: string[];
    includedTeethIds?: string[];
    showIncipientCariesRed?: boolean;
  },
): AnnotationsProps => {
  const boundingBoxes: BoundingBoxAnnotationProps[] = img.teeth
    .filter(
      (tooth) =>
        !options.includedTeethIds ||
        options.includedTeethIds.includes(tooth.toothId),
    )
    .flatMap((tooth) => {
      const boxes: BoundingBoxAnnotationProps[] = [];

      if (options.enabledFindings.includes('caries')) {
        const caries = getCariesFindings({
          caries: tooth.caries,
          showIncipientCariesRed: options.showIncipientCariesRed,
        });
        boxes.push(...caries);
      }

      if (options.enabledFindings.includes('restorative_adjacent_lucency')) {
        const lucencies = getRestorativeAdjacentLucencyFindings({
          restorativeAdjacentLucency: tooth.restorativeAdjacentLucency,
        });

        boxes.push(...lucencies);
      }

      return boxes || [];
    });

  const ovals: BoundingBoxAnnotationProps[] = img.teeth.flatMap((tooth) => {
    if (!options.enabledFindings.includes('calculus')) {
      return [];
    }

    return getCalculusFindings({ calculusFindings: tooth.calculus });
  });

  const lineCoordinates: LineAnnotationProps[] = img.teeth.flatMap((tooth) => {
    if (!options.enabledFindings.includes('radiographic_boneloss')) {
      return [];
    }

    const rbl = getRblLines({
      boneLevels: tooth.boneLevels,
    });

    return rbl;
  });

  const pointCoordinates: PointAnnotationProps[] = img.teeth.flatMap(
    (tooth) => {
      if (!options.enabledFindings.includes('radiographic_boneloss_points')) {
        return [];
      }

      const rblPoints = getRblPoints({
        boneLevels: tooth.boneLevels,
      });

      return rblPoints;
    },
  );

  return {
    boundingBoxes,
    lines: lineCoordinates,
    points: pointCoordinates,
    ovals,
  };
};
